import { useTranslation } from 'react-i18next';
import { formatPoints } from '@kidshome/i18n';
import { cn } from '@kidshome/ui';
import { useHouseholdContext } from '@/shared/lib/household';
import { StarIcon } from '@/shared/ui/StarIcon';

/**
 * An effort-star (✨) amount chip — a localized plain integer (never currency, ADR-0022) + a star glyph,
 * in the dedicated VIOLET currency tone (`text-star` / `bg-star-fg`) that keeps ✨ unmistakable from coins
 * (gold) and cash (green) — the three-currency separation from the parent deck. The guardian-facing
 * counterpart to {@link CoinChip}; the ✨ shown here is the PLAIN star, not the kid's hero skin (ADR-0103).
 */
export function StarChip({
  stars,
  size = 'md',
  className,
}: {
  stars: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const { locale } = useHouseholdContext();
  const value = formatPoints(locale, stars);
  // The full "N stars" label backs the chip for assistive tech (a screen reader announces "10 stars"
  // rather than a bare number + an undescribed glyph). The visible icon + number is `aria-hidden`.
  const label = t('common.starsLabel', { count: stars });
  const sizeCls =
    size === 'lg'
      ? 'text-md px-3 py-1.5'
      : size === 'sm'
        ? 'text-xs px-2 py-0.5'
        : 'text-sm px-2.5 py-1';
  return (
    <span
      role="img"
      aria-label={label}
      className={cn(
        'inline-flex items-center gap-1 rounded-pill bg-star-fg font-semibold text-star',
        sizeCls,
        className,
      )}
    >
      <StarIcon />
      <span aria-hidden="true">{value}</span>
    </span>
  );
}
